import React, { useEffect } from "react"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import ModalPortal from "../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { EEnableStatusType } from "../../../Enum/EEnableStatusType"
import { NoticeEntity } from "../../../Objects/Notice/NoticeEntity"
import THEME from "../../../configuration/Theme"
import { faBullhorn, faClose, faFile, faFileSignature } from "@fortawesome/free-solid-svg-icons"
import { ButtonIcon } from "../../../components/Button/Button"
import { SIZE } from "../../../configuration/Sizes"
import InputField from "../../../components/InputField/InputField"
import GetSpace from "../../../utils/space"
import Button from "../../../components/Button/Button"
import { useCreateNotice } from "../../../hooks/Notice/useCreateNotice"
import { useFileImage } from "../../../hooks/useFileImage"
import { InputPhotoWithContainer } from "../../../components/InputPhoto/InputPhoto"
import { useSaveNotice } from "../../../hooks/Notice/useSaveNotice"
import { useGetNotice } from "../../../hooks/Notice/useGetNotice"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { useRemoveNotice } from "../../../hooks/Notice/useRemoveNotice"
import { Permissions } from "../../../Enum/PermissionsType"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { CanView } from "../../../components/CanView/CanView"
import { CopyDraftAlert } from "../../../components/CopyDraftAlert/CopyDraftAlert"
import { useRegister } from "../../../hooks/Register/useRegister"
import { ViewRegister } from "../../../components/ViewRegister/ViewRegister"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { useTranslation } from "react-i18next"

interface NoticePopupProps {
  title: string
  notice?: NoticeEntity
  onClose: () => void
  onCopy?: () => void
}

export const NoticePopup: React.FC<NoticePopupProps> = ({ title, notice, onClose, onCopy }) => {
  const { t } = useTranslation("frontend")

  const canEdite = !usePermission(Permissions.CanEditeNotices)

  const isCopiedDraft = notice?.id === -1

  const canViewRegister = !!notice && notice.id !== -1

  const { handleEnableRegister, handleDisableRegister, isRegisterEnable } = useRegister()

  const { setNewConfirmation } = useConfirmation()

  const { handleGetNotice } = useGetNotice()

  const { handleRemove } = useRemoveNotice()

  const { handleSaveNotice } = useSaveNotice()

  const { fileImage, setFileImage } = useFileImage()

  const {
    name,
    description,
    status,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    handleInitialValue,
    handleSubmitForm,
    handleNameChange,
    handleDescriptionChange,
    handleStatusChange,
  } = useCreateNotice()

  useEffect(() => {
    handleInitialValue(notice)
  }, [notice])

  const handleCopyClick = async () => {
    setFileImage(null)

    onCopy?.()
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <section className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={title}
              icon={faBullhorn}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  {notice && notice.id !== -1 && (
                    <div style={{ width: "100px", marginRight: "5px" }}>
                      <GenericDropDown
                        data={Object.mapEnumToStrings(EEnableStatusType, { exclude: [EEnableStatusType.All] })}
                        initialState={status}
                        disable={canEdite}
                        handlerFuncion={handleStatusChange}
                        descriptionKey="interactionDescriptions:pages.serivesDir.notices.noticePopup.select.select"
                      />
                    </div>
                  )}

                  <PopupHelpButton
                    helpKey="pages.serivesdir.notices.noticepopup.1"
                    variant="global"
                    title={t("helpPopup:global.notice.title")}
                    description={t("helpPopup:global.notice.description")}
                    bullets={t("helpPopup:global.notice.bullets", { returnObjects: true }) as string[]}
                  />

                  <GetSpace right={5} />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.serivesDir.notices.noticePopup.button.onClose"
                  />
                </div>
              }
            />

            {isCopiedDraft && <CopyDraftAlert />}

            <form
              onSubmit={(e) => {
                e.preventDefault()
                handleSaveNotice(handleSubmitForm(), fileImage, () => {
                  onClose()
                  handleGetNotice(() => {})
                })
              }}
              className="p-2"
            >
              <InputPhotoWithContainer
                initialPhoto={notice?.iconUrl}
                disable={canEdite}
                handleFileSelected={setFileImage}
                imageProfile="notice"
              />

              <div className={defaultStyle.divisionBar}></div>

              <div className="grid md:grid-cols-2 gap-2">
                <InputField
                  type="date"
                  icon={faBullhorn}
                  value={startDate}
                  title="Data inicial"
                  placeholder="Data inicial"
                  onInvalidMessage="Informe a data inicial"
                  onChange={(e) => setStartDate(e.target.value)}
                  disable={canEdite}
                  required
                  descriptionKey="interactionDescriptions:pages.serivesDir.notices.noticePopup.input.dataInicial"
                />

                <InputField
                  type="date"
                  icon={faBullhorn}
                  value={endDate}
                  title="Data final"
                  placeholder="Data final"
                  onInvalidMessage="Informe a data final"
                  onChange={(e) => setEndDate(e.target.value)}
                  disable={canEdite}
                  required
                  descriptionKey="interactionDescriptions:pages.serivesDir.notices.noticePopup.input.dataFinal"
                />
              </div>

              <div className={defaultStyle.divisionBar}></div>

              <InputField
                type="text"
                icon={faFileSignature}
                value={name}
                placeholder="Título do aviso"
                onInvalidMessage="Informe um título."
                onChange={handleNameChange}
                disable={canEdite}
                required
                descriptionKey="interactionDescriptions:pages.serivesDir.notices.noticePopup.input.tituloDoAviso"
              />

              <GetSpace top={10} />

              <InputField
                type="text"
                icon={faFile}
                value={description}
                placeholder="Mensagem do aviso"
                onInvalidMessage="Informe a mensagem."
                onChange={handleDescriptionChange}
                disable={canEdite}
                required
                descriptionKey="interactionDescriptions:pages.serivesDir.notices.noticePopup.input.mensagemDoAviso"
              />

              <GetSpace top={10} />

              <div className={defaultStyle.divisionBar}></div>

              <div className={defaultStyle.buttonsController}>
                <CanView permission={Permissions.CanEditeNotices}>
                  <Button
                    label="Salvar"
                    typeButton="submit"
                    type="primary"
                    size="medium"
                    bgColor={THEME.colors.buttonBGSaveColor}
                    descriptionKey="interactionDescriptions:pages.serivesDir.notices.noticePopup.button.salvar"
                  />
                </CanView>

                {canViewRegister && (
                  <>
                    <GetSpace right={5} />

                    <Button
                      label="Registros"
                      typeButton="button"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGRegisterColor}
                      onClick={handleEnableRegister}
                      descriptionKey="interactionDescriptions:pages.serivesDir.notices.noticePopup.button.registros"
                    />
                  </>
                )}

                {notice && notice.id !== -1 && (
                  <CanView permission={Permissions.CanEditeNotices}>
                    <GetSpace right={5} />

                    {onCopy && (
                      <>
                        <Button
                          label="Copiar"
                          typeButton="button"
                          type="primary"
                          size="medium"
                          bgColor={THEME.colors.buttonBGPlusColor}
                          onClick={handleCopyClick}
                          descriptionKey="interactionDescriptions:pages.serivesDir.notices.noticePopup.button.copiar"
                        />
                        <GetSpace right={5} />
                      </>
                    )}

                    <Button
                      label="Remover"
                      typeButton="button"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGRemoveColor}
                      onClick={() =>
                        setNewConfirmation("Remover aviso", "Deseja remover este aviso?", async () => {
                          await handleRemove(notice, () => handleGetNotice(onClose))
                        })
                      }
                      descriptionKey="interactionDescriptions:pages.serivesDir.notices.noticePopup.button.remover"
                    />
                  </CanView>
                )}
              </div>
            </form>

            {isRegisterEnable && notice && (
              <ViewRegister id={notice.id} typeEntity="NoticeEntityId" onClose={handleDisableRegister} />
            )}
          </section>
        </Animate>
      </div>
    </ModalPortal>
  )
}
